import { useState, useCallback, useEffect, useRef } from 'react';
import { useController } from 'react-hook-form';
import { debounce } from 'lodash';
import { searchAddressSuggestions } from '../services/addressService';

// Default RHF field names. All add/edit forms (household, franchisee, supplier)
// share these, so callers can rely on the defaults.
const DEFAULT_FIELDS = {
  line1: 'addressLine1',
  line2: 'addressLine2',
  state: 'state',
  postalCode: 'postalCode',
};

/**
 * Reusable Australian-address autocomplete for react-hook-form fields.
 *
 * Renders one grid cell (label + input + suggestions dropdown) to replace an
 * existing "Address Line 1" input. Selecting a suggestion writes back Address
 * Line 1/2, State and Postal Code via RHF setValue; the sibling inputs for
 * those fields stay in the parent form and are populated here.
 *
 * Uses useController so only this component re-renders as the user types (the
 * parent form isn't subscribed via watch), and so the RHF ref/validation live
 * on the visible input.
 *
 * @param {object}   control  - RHF control (from useForm)
 * @param {Function} setValue - RHF setValue (writes the sibling address fields)
 * @param {boolean}  [required] - mark the field required + validate
 */
const AddressAutocomplete = ({
  control,
  setValue,
  label = 'Address Line 1',
  required = false,
  placeholder = 'Start typing an address...',
  id = 'addressLine1',
  fields = DEFAULT_FIELDS,
  setValueOptions = { shouldDirty: true, shouldValidate: true },
}) => {
  const { field, fieldState } = useController({
    control,
    name: fields.line1,
    rules: required ? { required: `${label} is required.` } : {},
  });

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [searchError, setSearchError] = useState(false);

  // Monotonic request id: a newer query or a selection bumps it, so a slower
  // in-flight response can't reopen the dropdown over an already-picked value.
  const requestIdRef = useRef(0);

  const runSearch = async (query) => {
    const requestId = ++requestIdRef.current;
    setIsSearching(true);
    setSearchError(false);
    try {
      const results = await searchAddressSuggestions(query);
      if (requestId !== requestIdRef.current) return; // superseded — ignore
      const trimmed = results.slice(0, 8); // Limit to 8 results
      setSuggestions(trimmed);
      setNoResults(trimmed.length === 0);
      setShowSuggestions(true);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      setNoResults(false);
      setSearchError(true);
      setShowSuggestions(true);
    } finally {
      if (requestId === requestIdRef.current) setIsSearching(false);
    }
  };

  const debouncedSearch = useCallback(debounce(runSearch, 300), []);
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const closeDropdown = () => {
    requestIdRef.current += 1; // invalidate any in-flight request
    debouncedSearch.cancel();
    setSuggestions([]);
    setNoResults(false);
    setSearchError(false);
    setShowSuggestions(false);
    setIsSearching(false);
  };

  const handleChange = (e) => {
    field.onChange(e); // keep the RHF value (and dirty state) in sync
    const next = e.target.value;
    if (next && next.length >= 3) {
      debouncedSearch(next);
    } else {
      closeDropdown();
    }
  };

  const handleSelect = (address) => {
    requestIdRef.current += 1; // invalidate any in-flight request
    debouncedSearch.cancel();
    const fmt = address.householdFormat || {};
    // The suggestion is one address: write all four fields together so State /
    // Postal Code / Line 2 always match the chosen Line 1.
    setValue(fields.line1, fmt.addressLine1 || '', setValueOptions);
    setValue(fields.line2, fmt.addressLine2 || '', setValueOptions);
    setValue(fields.state, fmt.state || address.state || '', setValueOptions);
    setValue(fields.postalCode, fmt.postalCode || address.postcode || '', setValueOptions);
    setSuggestions([]);
    setNoResults(false);
    setSearchError(false);
    setShowSuggestions(false);
  };

  const error = fieldState.error;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}{required ? ' *' : ''}
      </label>
      <div className="relative">
        <input
          id={id}
          type="text"
          ref={field.ref}
          value={field.value || ''}
          onChange={handleChange}
          onBlur={() => { field.onBlur(); setTimeout(() => setShowSuggestions(false), 150); }}
          onFocus={() => { if (suggestions.length > 0 || noResults || searchError) setShowSuggestions(true); }}
          autoComplete="new-password"
          name={`${id}-autocomplete-off`}
          placeholder={placeholder}
          className={`mt-1 block w-full px-3 py-2 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
        />
        {showSuggestions && (suggestions.length > 0 || noResults || searchError) && (
          <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg">
            {suggestions.length > 0 ? (
              <ul className="max-h-60 rounded-md text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                {suggestions.map((address, index) => (
                  <li
                    key={address.placeId ? `${address.placeId}-${index}` : index}
                    className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                    onMouseDown={() => handleSelect(address)}
                  >
                    <span className="block truncate">{address.description}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-md text-sm ring-1 ring-black ring-opacity-5 py-2 px-3 text-gray-500">
                {searchError ? 'Could not search addresses. Please try again.' : 'No addresses found.'}
              </div>
            )}
          </div>
        )}
      </div>
      {isSearching && <p className="mt-1 text-xs text-gray-400">Searching addresses…</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error.message}</p>}
    </div>
  );
};

export default AddressAutocomplete;
